/// <reference path="modal.module.ts" />
/// <reference path="modal.controller.ts" />
///<reference path="../short-cuts/index.d.ts"/>


module Meevo.Components.Modal {

    export class ModalService implements IModalService {
        private modal: angular.material.IDialogService;
        private translate: angular.translate.ITranslateService;
        private removeCallback = null;

        constructor(
            $mdDialog: angular.material.IDialogService, 
            $translate: angular.translate.ITranslateService, 
            protected $sce: angular.ISCEService,
            protected $mdSelect
        ) {
            this.modal = $mdDialog;
            this.translate = $translate;
        }

        /**
        * Sets the EMOVECALLBACK for the Modal.
        * @param {func} callback if any
        */
        // Below method is to enable scrolling right after modal close.. BUG #69325
        public setRemoveCallback(removeCallback: any = null) {
            this.removeCallback = removeCallback;
        }
        /**
        * Shows an ALERT modal containing the supplied message.
        * @param {string} contentKey
        * @return {IPromise<boolean>}
        */
        public alert(contentKey: string, translationMode: ModalTranslationModes = ModalTranslationModes.Translate, canShowMultiple: boolean = false, userOptions: ILocalOptions = undefined, headerContentKey: string = undefined): angular.IPromise<boolean> {
            return this.openModal(contentKey, ModalTypeEnum.Alert, translationMode, false, canShowMultiple, userOptions, headerContentKey);
        }

        /**
        * Shows a CONFIRM modal containing the supplied message.
        * @param {string} contentKey
        * @return {IPromise<boolean>}
        */
        public confirm(contentKey: string, translationMode: ModalTranslationModes = ModalTranslationModes.Translate, canShowMultiple: boolean = false, userOptions: ILocalOptions = undefined, processNegativeConfirm: boolean = false): angular.IPromise<boolean> {
            return this.openModal(contentKey, ModalTypeEnum.Confirm, translationMode, processNegativeConfirm, canShowMultiple, userOptions);
        }

        public yellowConfirm(contentKey: string, translationMode: ModalTranslationModes = ModalTranslationModes.Translate, canShowMultiple: boolean = false, userOptions: ILocalOptions = undefined, processNegativeConfirm: boolean = false): angular.IPromise<boolean> {
            const modalOptions = {
                ...userOptions,
                modalClass: 'modal-alert'
            };
            return this.openModal(contentKey, ModalTypeEnum.Confirm, translationMode, processNegativeConfirm, canShowMultiple, modalOptions);
        }

        public redAlertConfirm(contentKey: string, translationMode: ModalTranslationModes = ModalTranslationModes.Translate, canShowMultiple: boolean = false, userOptions: ILocalOptions = undefined): angular.IPromise<boolean> {
            const modalOptions = {
                ...userOptions,
                modalClass: 'modal-warning',
                showCancelButton: true,
            };
            return this.openModal(contentKey, ModalTypeEnum.Alert, translationMode, false, canShowMultiple, modalOptions, 'LABEL_ALERT');
        }

        /**
        * Shows a CONFIRM modal containing the supplied message, and allows to handle dialog result as boolean.
        * @param {string} contentKey
        * @return {IPromise<boolean>}
        */
        public confirmHandler(contentKey: string, translationMode?: ModalTranslationModes, canShowMultiple: boolean = false): angular.IPromise<boolean> {
            return this.openModal(contentKey, ModalTypeEnum.Confirm, translationMode, true, canShowMultiple);
        }

        public okCancelHandler(contentKey: string, translationMode?: ModalTranslationModes, canShowMultiple: boolean = false, userOptions: ILocalOptions = undefined): angular.IPromise<boolean> {
            return this.openModal(contentKey, ModalTypeEnum.OkCancel, translationMode, true, canShowMultiple, userOptions);
        }

        /**
        * Shows an ERROR modal containing the supplied message.
        * @param {string} contentKey
        * @return {IPromise<boolean>}
        */
        public error(contentKey: string, translationMode: ModalTranslationModes = ModalTranslationModes.Translate, canShowMultiple: boolean = false): angular.IPromise<boolean> {
            return this.openModal(contentKey, ModalTypeEnum.Error, translationMode, false, canShowMultiple);
        }

        /**
        * Shows a WARNING modal containing the supplied message.
        * @param {string} contentKey
        * @return {IPromise<boolean>}
        */
        public warning(contentKey: string, translationMode: ModalTranslationModes = ModalTranslationModes.Translate, canShowMultiple: boolean = false, userOptions: ILocalOptions = undefined): angular.IPromise<boolean> {
            return this.openModal(contentKey, ModalTypeEnum.Warning, translationMode, false, canShowMultiple, userOptions);
        }

        /**
        * Shows an INFO modal containing the supplied message.
        * @param {string} contentKey
        * @return {IPromise<boolean>}
        */
        public info(contentKey: string, translationMode: ModalTranslationModes = ModalTranslationModes.Translate, canShowMultiple: boolean = false, userOptions: ILocalOptions = undefined, headerContentKey: string = undefined): angular.IPromise<boolean> {
            return this.openModal(contentKey, ModalTypeEnum.Info, translationMode, false, canShowMultiple, userOptions, headerContentKey);
        }

        /**
         * Shows an MESSAGE modal containing the supplied message.
         */
        public message(contentKey: string, translationMode: ModalTranslationModes = ModalTranslationModes.Translate, canShowMultiple: boolean = false): angular.IPromise<boolean> {
            return this.openModal(contentKey, ModalTypeEnum.Message, translationMode, false, canShowMultiple);
        }

        /**
        * Shows a CUSTOM MODAL based on the supplied IModalOptions.
        * @param {IModalOptions} options - provide the ability to specify a custom template, controller, etc.
        * @return {IPromise<any>}
        */
        public showCustom(options: IModalOptions, translationMode: ModalTranslationModes = ModalTranslationModes.DoNotTranslate, canShowMultiple: boolean = false): angular.IPromise<any> {
            options.escapeToClose = false;
            if (canShowMultiple) {
                options.multiple = true;
            }
            if (translationMode === ModalTranslationModes.Translate) {
                options.locals["message"] = this.translate.instant(options.locals["message"], options.locals["customState"]);
            }
            else if (translationMode === ModalTranslationModes.AsHtml) {
                options.locals["message"] = this.$sce.trustAsHtml(options.locals["message"]);
            }
            if (options.additionalClassName && !options.onShowing) {
                options.onShowing = function (_modalScope: ng.IScope, $el) {
                    if ($el && $el[0] instanceof HTMLElement) {
                        // classList.add throws on a token containing whitespace, so split first.
                        ($el[0] as HTMLElement).classList.add(...options.additionalClassName.split(/\s+/).filter(Boolean));
                    }
                }
            }
            this.closeControlDropdowns();
            return this.modal.show(options);
        }

        /**
        * Shows a NOTE modal containing the supplied message.
        * @param {string} contentKey
        * @return {IPromise<boolean>}
        */
        public note(contentKey: string, translationMode: ModalTranslationModes = ModalTranslationModes.Translate, canShowMultiple: boolean = false): angular.IPromise<boolean> {
            return this.openModal(contentKey, ModalTypeEnum.Note, translationMode, false, canShowMultiple);
        }

        /**
        * Shows a NOTE REG modal containing the supplied message.
        * @param {string} options that contain list of Notes to process
        *   locals: notes : { displayName: string, message: string, isAppointmentType: boolean, isRegisterType: boolean }
        * @return {IPromise<boolean>}
        */
        public noteRegister(options: IModalOptions, translationMode: ModalTranslationModes = ModalTranslationModes.Translate, canShowMultiple:boolean=false): angular.IPromise<boolean> {
            options.escapeToClose = false;
            options.controllerAs = "vm";
            options.bindToController = true;
            options.preserveScope = true;
            options.hasBackdrop = false;
            options.multiple = true;
            options.locals.modalType = Components.Modal.ModalTypeEnum.NoteRegister;
            if (translationMode !== ModalTranslationModes.DoNotTranslate) {
                _(options.locals.notes).each((note: any) => {
                    if (translationMode === ModalTranslationModes.Translate) {
                        note.message = this.translate.instant(note.message);
                    }
                    else if (translationMode === ModalTranslationModes.AsHtml) {
                        note.message = this.$sce.trustAsHtml(note.message);
                    }
                });
            }
            this.closeControlDropdowns();
            return this.modal.show(options);
        }

        public obConfirm(contentKey: string): ng.IPromise<boolean> {
            return this.openModal(contentKey, ModalTypeEnum.OBConfirm, ModalTranslationModes.Translate, false, false, {buttons: ['yes', 'no']});
        }

        public hide() {
            return this.modal.hide(false);
        }

        public hideWithResponse(response) {
            return this.modal.hide(response);
        }

        public cancel() {
            this.modal.cancel(false);
        }

        /**
        * Common function to open any non-custom modal.
        * translationMode: An optional parameter (defaults to translate) that indicates if the text in the contentKey should be used to
        * look up the translation or passed through as is
        * @return {IPromise<boolean>}
        */
        private openModal(contentKey: string, modalType: ModalTypeEnum, translationMode: ModalTranslationModes = ModalTranslationModes.Translate, processNegativeConfirm: boolean = false, canShowMultiple: boolean = false, userOptions: ILocalOptions = undefined,
            headerContentKey: string = undefined): angular.IPromise<boolean> {
            this.isModalOverlapping();

            let resolvedMessage = contentKey;
            if (translationMode === ModalTranslationModes.Translate) {
                resolvedMessage = this.translate.instant(contentKey);
            }
            let options = this.getModalOptions(resolvedMessage, modalType, processNegativeConfirm, canShowMultiple, userOptions, headerContentKey);
            options.escapeToClose = false;

            if (translationMode === ModalTranslationModes.AsHtml) {
                options.locals["message"] = this.$sce.trustAsHtml(resolvedMessage);
            }

            this.closeControlDropdowns();
            let promise = this.modal.show(options);
            return promise.then((pdata: any) => { this.undoModalOverlapping(); return pdata; });
        }

        /**
        * Common function to set the options for any non-custom modal.
        * @return {IPromise<boolean>}
        */
        //  skipHide: true,
        private getModalOptions(message: string, modalType: ModalTypeEnum, processNegativeConfirm: boolean = false, canShowMultiple: boolean = false, userOptions: Object = undefined,
            headerContentKey: string = undefined): IModalOptions {
            let options: IModalOptions = {
                templateUrl: "app/components/modal/modal.template.html",
                controller: "meevo.components.modal.ModalController",
                controllerAs: "vm",
                bindToController: true,
                preserveScope: true,
                hasBackdrop: false,
                multiple: canShowMultiple,
                locals: {
                    message: message,
                    modalType: modalType,
                    processNegativeConfirm: processNegativeConfirm,
                    isRegister: false,
                    headerContentKey: headerContentKey,
                    ...{ userOptions }
                },
               // onRemoving: this.removeCallback
            }
            return options;
        }

        private isModalOverlapping() {
            var myEl = angular.element(document.querySelector('.md-dialog-container'));
            if (myEl.length === 1) {
                myEl.addClass('customModalPosition');
            }
        }

        private undoModalOverlapping() {
            var myEl = angular.element(document.querySelector('.md-dialog-container'));
            if (myEl.length === 1) {
                myEl.removeClass('customModalPosition');
            }
        }

        private closeControlDropdowns() {
            // hide any open md-select dropdowns
            this.$mdSelect.hide();

            // remove any open datepicker calendars (gets recreated)
            $('.md-scroll-mask, .md-datepicker-calendar-pane').remove();
        }

        /**
        * Factory method used to get an instance of the modal service.
        */
        static factory(): Function {
            let svc = (
                $mdDialog: angular.material.IDialogService, 
                $translate: angular.translate.ITranslateService, 
                $sce: angular.ISCEService,
                $mdSelect
            ) => new ModalService(
                $mdDialog, 
                $translate, 
                $sce, 
                $mdSelect
            );

            svc.$inject = [
                "$mdDialog", 
                "$translate", 
                "$sce", 
                "$mdSelect"
            ];

            return svc;
        }
    }

    angular.module("meevo.components.modal")
        .factory("meevo.components.modal.ModalService", ModalService.factory());
}
